import * as THREE from 'three';

/**
 * A world-aligned box collider. Buildings/walls/curbs are authored as several
 * of these (not one solid box per building) so doorways/alleys/gaps are just
 * "the wall segment that isn't there" — no boolean-subtraction geometry.
 */
export interface BoxCollider {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Top surface height, world Y. Used by vault (§ below) to test ledge height. */
  topY: number;
  /** Bottom of the solid, world Y. A raised curb's bottom can be > 0. */
  bottomY: number;
}

/**
 * `priority` breaks ties between overlapping regions that both pass the
 * height/tolerance gate below — every named region defaults to 1 EXCEPT
 * `STREET_FLOOR` (0), which deliberately blankets the whole map as a
 * fallback. Without this, "pick the highest qualifying region" can never
 * select a DESCENDING region (a ramp or stairs going below street level):
 * street's height (0) is always ≥ anything below it, so it wins the
 * height comparison at every single step of a descent and the player can
 * never walk down into the tunnel or subway at all (confirmed: a straight
 * walk from the utility yard through the tunnel ramp's full footprint and
 * deep into the tunnel floor never moved off y=0). Priority fixes this
 * uniformly for both directions — an ascending region (rooftop, fire
 * escape) already won on height once climbed high enough, so giving it
 * priority 1 changes nothing there; a descending region now wins on
 * priority the INSTANT it qualifies by height, not only once it happens
 * to exceed street's height (which a descent, by definition, never does).
 */
const DEFAULT_PRIORITY = 1;

/** A flat walkable surface. Ground height is constant across the whole footprint. */
export interface FlatFloor {
  kind: 'flat';
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  y: number;
  priority?: number;
}

/**
 * A walkable incline. Height interpolates linearly along one axis between
 * y0 (at the axis's min edge) and y1 (at the max edge) — ASSET-CONTRACT §5's
 * own rule ("ramp-approximated stairs, no stepped collision") is exactly the
 * shape this models, so it doubles as both the fire-escape ramp and the
 * subway stair.
 */
export interface RampFloor {
  kind: 'ramp';
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  axis: 'x' | 'z';
  /** Height at the axis's min edge. */
  y0: number;
  /** Height at the axis's max edge. */
  y1: number;
  priority?: number;
}

export type FloorRegion = FlatFloor | RampFloor;

/**
 * The minimum rise `findVaultLedge` treats as a real obstacle worth an
 * explicit vault, as opposed to floor noise. Deliberately its OWN constant,
 * separate from the `stepTolerance` callers pass in — that parameter serves
 * `groundHeightAt`'s very different job (excluding a floor region many
 * meters above, like a rooftop, while walking underneath it) where its
 * exact value barely matters; reusing it here as the vault-rise floor would
 * silently reject any prop shorter than whatever that tolerance happens to
 * be (confirmed: this project's own 0.45-0.5m park props sat exactly at
 * that boundary and would never have registered as vaultable).
 */
const MIN_VAULTABLE_RISE = 0.12;

function floorHeightAt(region: FloorRegion, x: number, z: number): number {
  if (region.kind === 'flat') return region.y;
  const t =
    region.axis === 'x'
      ? THREE.MathUtils.clamp((x - region.minX) / (region.maxX - region.minX), 0, 1)
      : THREE.MathUtils.clamp((z - region.minZ) / (region.maxZ - region.minZ), 0, 1);
  return THREE.MathUtils.lerp(region.y0, region.y1, t);
}

function containsXZ(region: FloorRegion, x: number, z: number): boolean {
  return x >= region.minX && x <= region.maxX && z >= region.minZ && z <= region.maxZ;
}

/**
 * The block's full physical layout: floors (multi-level ground/ramps/roof/
 * tunnel) + walls (horizontal blockers). Kept as plain data + pure functions
 * rather than a class with hidden state, so the harness and dev tools can
 * poke at it directly via the debug hook.
 */
export class BlockPhysics {
  constructor(
    private readonly floors: FloorRegion[],
    private readonly walls: BoxCollider[],
  ) {}

  /**
   * The standing height at (x,z) given the capsule is currently near `nearY`.
   * Multiple floors can overlap in XZ (a rooftop sits directly above the
   * alley it's reached from) — among every region whose height does not
   * exceed `nearY + stepTolerance` (excludes the roof while walking
   * underneath it, since roof height is way above nearY+tolerance; lets it
   * win once the player has actually climbed up near roof height), pick the
   * HIGHEST-PRIORITY one, using height only as the tie-break within equal
   * priority — see `DEFAULT_PRIORITY`'s comment for why this can't just be
   * "pick the highest height" (that rule can never select a descending
   * ramp/stair over the ubiquitous street plane). Falls back to `null` when
   * nothing qualifies (open air — caller keeps falling).
   */
  groundHeightAt(x: number, z: number, nearY: number, stepTolerance: number): number | null {
    let bestHeight: number | null = null;
    let bestPriority = -Infinity;
    for (const region of this.floors) {
      if (!containsXZ(region, x, z)) continue;
      const h = floorHeightAt(region, x, z);
      if (h > nearY + stepTolerance) continue;
      const priority = region.priority ?? DEFAULT_PRIORITY;
      if (bestHeight === null || priority > bestPriority || (priority === bestPriority && h > bestHeight)) {
        bestHeight = h;
        bestPriority = priority;
      }
    }
    return bestHeight;
  }

  /**
   * Resolve a proposed horizontal move against every wall whose vertical
   * span overlaps the capsule's current standing height — a wall segment
   * with topY below the capsule's feet (a low curb once you're already on
   * the sidewalk above it) or bottomY above the capsule's head doesn't
   * block.
   *
   * Circle-vs-AABB via minimum-penetration push-out: treat the wall as
   * expanded by `radius` on every side (a plus-shaped Minkowski
   * approximation, not a true rounded rect — corners are a hair more
   * generous than an exact circle-vs-box test; acceptable for gray-box
   * collision, per §0.4). For a point found inside that expanded box, push
   * out along whichever of the 4 edges is closest, resolving X and Z
   * TOGETHER per wall rather than as two passes over stale coordinates.
   * An earlier two-pass version (resolve X using the old Z, then Z using
   * the new X) had a real bug: approaching a wall dead-on with the capsule
   * already centered on the wall's X-range makes the X-pass's own
   * "distance to closest point" exactly 0, and `distX >= 0` then defaulted
   * to shoving the player to `wall.maxX + radius` — a multi-meter sideways
   * teleport on pure straight-ahead movement, even though the real
   * penetration was entirely on Z. Comparing actual penetration DEPTH on
   * all 4 sides at once, instead of inferring a push direction from a
   * possibly-zero/degenerate distance, doesn't have that failure mode.
   */
  resolveHorizontal(
    x: number,
    z: number,
    dx: number,
    dz: number,
    radius: number,
    feetY: number,
    headY: number,
  ): { x: number; z: number; blockedX: boolean; blockedZ: boolean } {
    let nx = x + dx;
    let nz = z + dz;
    let blockedX = false;
    let blockedZ = false;

    for (const wall of this.walls) {
      if (wall.topY <= feetY || wall.bottomY >= headY) continue; // vertically clear of this wall
      const expMinX = wall.minX - radius;
      const expMaxX = wall.maxX + radius;
      const expMinZ = wall.minZ - radius;
      const expMaxZ = wall.maxZ + radius;
      if (nx <= expMinX || nx >= expMaxX || nz <= expMinZ || nz >= expMaxZ) continue; // not overlapping this wall at all

      const penMinX = nx - expMinX;
      const penMaxX = expMaxX - nx;
      const penMinZ = nz - expMinZ;
      const penMaxZ = expMaxZ - nz;
      const minPen = Math.min(penMinX, penMaxX, penMinZ, penMaxZ);

      if (minPen === penMinX) {
        nx = expMinX;
        blockedX = true;
      } else if (minPen === penMaxX) {
        nx = expMaxX;
        blockedX = true;
      } else if (minPen === penMinZ) {
        nz = expMinZ;
        blockedZ = true;
      } else {
        nz = expMaxZ;
        blockedZ = true;
      }
    }
    return { x: nx, z: nz, blockedX, blockedZ };
  }

  /**
   * Find a wall directly ahead of (x,z) in the (dirX,dirZ) facing whose top
   * is a vaultable ledge: higher than the capsule's feet by more than the
   * step tolerance (a real obstacle, not just floor noise) but no higher
   * than maxVaultHeight, with clear headroom above it. Returns the ledge's
   * top height AND its footprint bounds (so the caller can clamp a fixed-
   * distance landing to stay on the ledge — see gotcha in controller.ts's
   * startVault: without this, a short-depth prop triggered near-range let
   * the landing point overshoot past its far edge), or null if nothing
   * ahead qualifies (either no wall, too tall to vault, or not enough
   * headroom to stand on it).
   */
  /**
   * True if a circle of `radius` centered at (x,z), spanning [feetY,headY]
   * vertically, overlaps any wall — the same vertical-clearance +
   * expanded-AABB test `resolveHorizontal` already applies per-wall, factored
   * out as a plain boolean query. S03 uses this to bake `NavGrid`'s walkable
   * mask (a grid cell must clear every wall a real enemy capsule would, not
   * just sit at street height) — kept here rather than duplicated in
   * navgrid.ts so the nav bake and real movement collision can never quietly
   * disagree about what counts as "blocked" (Classic gotcha #19's formula-
   * drift lesson).
   */
  isPointBlocked(x: number, z: number, radius: number, feetY: number, headY: number): boolean {
    for (const wall of this.walls) {
      if (wall.topY <= feetY || wall.bottomY >= headY) continue;
      if (x > wall.minX - radius && x < wall.maxX + radius && z > wall.minZ - radius && z < wall.maxZ + radius) {
        return true;
      }
    }
    return false;
  }

  /**
   * True if a straight ray from (x0,z0) to (x1,z1), at a fixed world height
   * `y`, is NOT blocked by any wall whose vertical span covers `y` — an
   * exact slab-method segment-vs-AABB test (radius 0: a line, not a swept
   * capsule), cheap enough at this wall count (~20) to run per-query rather
   * than sampling steps along the segment. Two S03 consumers: combat line-
   * of-sight (the ranged trash-can lobber won't lob through a building; the
   * dive-bomb pigeon won't dive through a roof) and nav-path string-pulling
   * (`NavGrid` collapsing a jagged grid-cell path into the fewest straight
   * segments that still avoid every wall).
   */
  segmentClear(x0: number, z0: number, x1: number, z1: number, y: number): boolean {
    const dx = x1 - x0;
    const dz = z1 - z0;
    walls: for (const wall of this.walls) {
      if (wall.topY <= y || wall.bottomY >= y) continue;
      let tMin = 0;
      let tMax = 1;
      const axes: Array<[number, number, number, number]> = [
        [dx, x0, wall.minX, wall.maxX],
        [dz, z0, wall.minZ, wall.maxZ],
      ];
      for (const [d, p0, lo, hi] of axes) {
        if (Math.abs(d) < 1e-9) {
          if (p0 < lo || p0 > hi) continue walls; // parallel to this slab and outside it — the segment can never enter the box
        } else {
          let t0 = (lo - p0) / d;
          let t1 = (hi - p0) / d;
          if (t0 > t1) [t0, t1] = [t1, t0];
          tMin = Math.max(tMin, t0);
          tMax = Math.min(tMax, t1);
          if (tMin > tMax) continue walls;
        }
      }
      return false; // this wall's box overlaps the segment's own [0,1] parameter range — blocked
    }
    return true;
  }

  findVaultLedge(
    x: number,
    z: number,
    dirX: number,
    dirZ: number,
    probeDist: number,
    radius: number,
    feetY: number,
    stepTolerance: number,
    maxVaultHeight: number,
    clearanceHeight: number,
  ): { y: number; minX: number; maxX: number; minZ: number; maxZ: number } | null {
    const px = x + dirX * probeDist;
    const pz = z + dirZ * probeDist;
    for (const wall of this.walls) {
      const closestX = THREE.MathUtils.clamp(px, wall.minX, wall.maxX);
      const closestZ = THREE.MathUtils.clamp(pz, wall.minZ, wall.maxZ);
      const distX = px - closestX;
      const distZ = pz - closestZ;
      if (distX * distX + distZ * distZ > radius * radius) continue;
      const rise = wall.topY - feetY;
      if (rise <= MIN_VAULTABLE_RISE || rise > maxVaultHeight) continue;
      if (wall.bottomY > feetY + stepTolerance) continue; // floating ledge, not grounded from below
      // headroom check: nothing else occupies the space just above the ledge
      const blocked = this.walls.some(
        (w2) =>
          w2 !== wall &&
          w2.bottomY < wall.topY + clearanceHeight &&
          w2.topY > wall.topY &&
          px >= w2.minX - radius &&
          px <= w2.maxX + radius &&
          pz >= w2.minZ - radius &&
          pz <= w2.maxZ + radius,
      );
      if (blocked) continue;
      return { y: wall.topY, minX: wall.minX, maxX: wall.maxX, minZ: wall.minZ, maxZ: wall.maxZ };
    }
    return null;
  }
}
